import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Camera, Video } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { VideoWall } from "@/components/VideoWall";

const Stars = ({ rating }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= rating ? "text-[#FBBC04] fill-[#FBBC04]" : "text-white/20"}`} />
    ))}
  </div>
);

export const Testimonials = () => {
  const { lang, t } = useLang();
  const s = t.testimonials;
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [google, setGoogle] = useState(null);
  const [photo, setPhoto] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [video, setVideo] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoPct, setVideoPct] = useState(0);
  const [videoErr, setVideoErr] = useState(false);

  const CHUNK = 2 * 1024 * 1024;
  const onVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setVideoErr(false);
    if (file.size > 60 * 1024 * 1024) {
      setVideoErr(true);
      return;
    }
    const rawExt = (file.name.split(".").pop() || "").toLowerCase();
    const ext = ["mp4", "webm", "mov"].includes(rawExt) ? rawExt : "mp4";
    setVideoBusy(true);
    setVideoPct(0);
    try {
      const { data: init } = await api.post("/uploads/review-video/init");
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", i);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post("/uploads/review-video/chunk", fd);
        setVideoPct(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await api.post("/uploads/review-video/complete", { upload_id: init.upload_id, ext });
      setVideo(data.path);
    } catch {
      setVideoErr(true);
    } finally {
      setVideoBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/review-photo", fd);
      setPhoto(res.data.path);
    } catch {
      // ignore
    } finally {
      setPhotoBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/testimonials/submit", { name, rating, text, photo_path: photo, video_path: video });
      setSent(true);
    } catch {
      // keep form
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/testimonials").then((res) => setReviews(res.data));
    api.get("/google-rating").then((r) => {
      if (r.data.rating > 0) setGoogle(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.pathname !== "/avis") return;
    const nom = new URLSearchParams(window.location.search).get("nom");
    if (nom) setName(nom.slice(0, 80));
    const tmr = setTimeout(() => {
      document.querySelector('[data-testid="review-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 700);
    return () => clearTimeout(tmr);
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section id="reviews" className="py-24 md:py-32 border-t border-white/10" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-6"
        >
          {s.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-display text-4xl md:text-5xl font-medium tracking-tight max-w-3xl mb-14"
        >
          {s.title}
        </motion.h2>

        {google && (
          <a
            href={google.url || undefined}
            target={google.url ? "_blank" : undefined}
            rel="noreferrer"
            data-testid="google-rating-badge"
            className={`inline-flex items-center gap-3 border border-white/15 bg-[#111111] px-5 py-3 rounded-full mb-12 ${google.url ? "hover:border-[#FF7900] transition-colors duration-300" : "cursor-default"}`}
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
            </svg>
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              {String(google.rating).replace(".", lang === "fr" ? "," : ".")}/5
              <Star className="w-4 h-4 text-[#FBBC04] fill-[#FBBC04]" />
            </span>
            <span className="text-sm text-white/50">
              {s.googleOn}{google.count > 0 ? ` · ${google.count} ${s.googleReviews}` : ""}
            </span>
          </a>
        )}

        <VideoWall reviews={reviews} />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="border border-white/10 bg-[#111111] p-7 flex flex-col gap-5 hover:border-[#FF7900]/40 transition-colors duration-500"
              data-testid={`testimonial-card-${r.id}`}
            >
              <Stars rating={r.rating} />
              <p className="text-sm text-white/75 leading-relaxed flex-1">“{r.text}”</p>
              {r.photo_path && (
                <img
                  src={`${api.defaults.baseURL}/review-photos/${r.photo_path}`}
                  alt={r.name}
                  className="mt-3 rounded-sm border border-white/10 max-h-36 object-cover"
                  data-testid={`testimonial-photo-${r.id}`}
                />
              )}
              {r.video_path && (
                <video
                  controls
                  preload="metadata"
                  src={`${api.defaults.baseURL}/review-videos/${r.video_path}`}
                  className="mt-3 rounded-sm border border-white/10 max-h-56 w-full bg-black"
                  data-testid={`testimonial-video-${r.id}`}
                />
              )}
              <div className="flex items-center gap-3 pt-1">
                <span className="w-10 h-10 rounded-full bg-[#FF7900]/15 text-[#FF7900] font-display font-semibold flex items-center justify-center">
                  {r.name.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-semibold">{r.name}</p>
                  <p className="text-xs text-white/35">
                    {new Date(r.created_at).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", {
                      year: "numeric",
                      month: "long",
                    })}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 border border-white/10 bg-[#111111] p-6 max-w-2xl" data-testid="review-form">
          {sent ? (
            <p className="text-sm text-emerald-400" data-testid="review-thanks">{s.thanks}</p>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF7900] mb-4">{s.leaveTitle}</p>
              <div className="flex gap-1.5 mb-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`} aria-label={`${i}/5`}>
                    <Star className={`w-6 h-6 transition-colors duration-200 ${i <= rating ? "text-[#FBBC04] fill-[#FBBC04]" : "text-white/20 hover:text-white/40"}`} />
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={s.yourName}
                  data-testid="review-name-input"
                  className="bg-black/40 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
                />
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={s.yourReview}
                rows={3}
                data-testid="review-text-input"
                className="w-full bg-black/40 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300 resize-none mb-4"
              />
              <button
                onClick={submit}
                disabled={busy || photoBusy || videoBusy || name.trim().length < 2 || text.trim().length < 10}
                data-testid="review-submit-button"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] disabled:opacity-40 text-sm font-semibold px-6 py-2.5 rounded-full transition-colors duration-300"
              >
                {s.submit}
              </button>
              <label className="ml-3 inline-flex items-center gap-2 text-xs text-white/50 hover:text-white/80 cursor-pointer transition-colors duration-300" data-testid="review-photo-label">
                <Camera className="w-4 h-4 text-[#FF7900]" />
                {photoBusy ? s.photoUploading : photo ? s.photoAttached : s.photoLabel}
                <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onPhoto} disabled={photoBusy} className="hidden" data-testid="review-photo-input" />
              </label>
              <label className="ml-3 inline-flex items-center gap-2 text-xs text-white/50 hover:text-white/80 cursor-pointer transition-colors duration-300" data-testid="review-video-label">
                <Video className="w-4 h-4 text-[#FF7900]" />
                {videoBusy ? `${s.videoUploading} ${videoPct}%` : video ? s.videoAttached : videoErr ? s.videoTooBig : s.videoLabel}
                <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onVideo} disabled={videoBusy} className="hidden" data-testid="review-video-input" />
              </label>
              <p className="mt-3 text-[11px] text-white/30">{s.moderation}</p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
